import { useCallback, useEffect, useRef, useState, type JSX } from 'react'
import { SendBundleDialog } from './SendBundleDialog'
import { FeedbackButton } from './FeedbackButton'
import { isTypingInField, matchesBundleShortcut } from './hotkey'
import type { SubmitResult } from './submit'

/**
 * Opens the report dialog from outside React's tree.
 *
 * The menu item lives in the header dropdown, several layers away from the host
 * and with no shared ancestor worth adding a context for. A module-level
 * subscription is the smaller seam.
 */
let requestOpen: (() => void) | null = null

export function openSendBundleDialog(): void {
  requestOpen?.()
}

export interface SupportBundleHostProps {
  /** Resolved feature flag; the shortcut and the menu item share this gate. */
  enabled: boolean
  hasModel: boolean
  /** Takes the screenshot. Awaited before the dialog renders. */
  onCapture: () => void | Promise<void>
  onSend: (
    message: string,
    options: { includeScreenshot: boolean; includeModel: boolean },
  ) => Promise<SubmitResult>
}

/**
 * Hosts the report dialog and owns the keyboard shortcut.
 *
 * Mounted app-wide rather than inside the editor: "the import wizard hung" and
 * "the dashboard won't load" are exactly the reports that cannot be reproduced
 * today, and off the editor the bundle simply carries no model.
 */
export function SupportBundleHost(props: SupportBundleHostProps): JSX.Element | null {
  const { enabled, hasModel, onCapture, onSend } = props
  const [open, setOpen] = useState(false)
  // Re-entrancy guard covering the window between the keystroke and the dialog
  // appearing: capture is awaited, so a second press can arrive before `open`
  // has flipped. Either way a draft in progress must never be reset.
  const opening = useRef(false)

  const openDialog = useCallback((): void => {
    if (open || opening.current) return
    opening.current = true
    void (async (): Promise<void> => {
      try {
        await onCapture()
      } finally {
        opening.current = false
        setOpen(true)
      }
    })()
  }, [open, onCapture])

  useEffect((): (() => void) | undefined => {
    if (!enabled) return undefined

    const onKeyDown = (event: KeyboardEvent): void => {
      if (!matchesBundleShortcut(event)) return
      if (isTypingInField(document.activeElement)) return
      // Ctrl+Shift+B is the browser's bookmarks-bar toggle.
      event.preventDefault()
      openDialog()
    }

    window.addEventListener('keydown', onKeyDown)
    requestOpen = openDialog
    return (): void => {
      window.removeEventListener('keydown', onKeyDown)
      requestOpen = null
    }
  }, [enabled, openDialog])

  if (!enabled) return null

  return (
    <>
      {/* The discoverable way in. The shortcut is invisible and the menu item
          was unreachable behind the header's stacking context, so without this
          a user who did not already know Ctrl+Shift+B had no route at all. */}
      <FeedbackButton onClick={openDialog} />
      <SendBundleDialog
        open={open}
        onOpenChange={setOpen}
        onSend={onSend}
        hasModel={hasModel}
      />
    </>
  )
}
